import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../config.js';

const WING_CONFIGS = [
  { id: 'PRE_PRIMARY', label: '🧸 Pre-Primary', sub: 'Nursery, LKG, UKG', classes: ['Nursery', 'LKG', 'UKG', 'Pre-KG'] },
  { id: 'PRIMARY', label: '🎒 Primary', sub: 'Class 1st – 5th', classes: ['1st', '2nd', '3rd', '4th', '5th'] },
  { id: 'MIDDLE_SCHOOL', label: '📚 Middle School', sub: 'Class 6th – 8th', classes: ['6th', '7th', '8th'] },
  { id: 'HIGH_SCHOOL', label: '🎓 High School', sub: 'Class 9th – 10th', classes: ['9th', '10th'] },
  { id: 'PUC', label: '🎓 PUC', sub: 'Class 11th – 12th / 1st-2nd PUC', classes: ['11th', '12th', '1st PUC', '2nd PUC'] }
];

const STANDARD_SCHOOL_CLASSES = [
  { name: 'Nursery', sections: ['A', 'B'] },
  { name: 'LKG', sections: ['A', 'B'] },
  { name: 'UKG', sections: ['A', 'B'] },
  { name: '1st', sections: ['A', 'B', 'C'] },
  { name: '2nd', sections: ['A', 'B', 'C'] },
  { name: '3rd', sections: ['A', 'B', 'C'] },
  { name: '4th', sections: ['A', 'B', 'C'] },
  { name: '5th', sections: ['A', 'B', 'C'] },
  { name: '6th', sections: ['A', 'B', 'C'] },
  { name: '7th', sections: ['A', 'B', 'C'] },
  { name: '8th', sections: ['A', 'B', 'C'] },
  { name: '9th', sections: ['A', 'B', 'C'] },
  { name: '10th', sections: ['A', 'B', 'C'] },
  { name: '11th', sections: ['A', 'B'] },
  { name: '12th', sections: ['A', 'B'] },
  { name: '1st PUC', sections: ['A', 'B'] },
  { name: '2nd PUC', sections: ['A', 'B'] }
];

export const isClassInWingCategory = (className, wingId) => {
  if (!className || !wingId) return false;
  const c = className.trim().toLowerCase();
  switch (wingId) {
    case 'PRE_PRIMARY':
      return c.includes('nurs') || c.includes('lkg') || c.includes('ukg') || c.includes('pre-kg') || c.includes('playgroup') || c.includes('pp1') || c.includes('pp2');
    case 'PRIMARY':
      return /^(1st|2nd|3rd|4th|5th|std\s*[1-5]|grade\s*[1-5]|class\s*[1-5]|[1-5])$/i.test(c) || ['1st', '2nd', '3rd', '4th', '5th'].includes(c);
    case 'MIDDLE_SCHOOL':
      return /^(6th|7th|8th|std\s*[6-8]|grade\s*[6-8]|class\s*[6-8]|[6-8])$/i.test(c) || ['6th', '7th', '8th'].includes(c);
    case 'HIGH_SCHOOL':
      return /^(9th|10th|std\s*(9|10)|grade\s*(9|10)|class\s*(9|10)|9|10)$/i.test(c) || ['9th', '10th'].includes(c);
    case 'PUC':
      return c.includes('puc') || /^(11th|12th|std\s*(11|12)|grade\s*(11|12)|class\s*(11|12)|11|12)$/i.test(c) || ['11th', '12th', '1st puc', '2nd puc'].includes(c);
    default:
      return false;
  }
};

const TeacherClassAccessModal = ({
  isOpen,
  onClose,
  teacher,
  orgId,
  academicYear = '2026-2027',
  token: propToken,
  onSaveSuccess
}) => {
  const [accessMode, setAccessMode] = useState('WING_SCOPED'); // ALL_CLASSES, WING_SCOPED, ALLOCATED_ONLY
  const [teachingWing, setTeachingWing] = useState('PRIMARY');
  const [selectedGrants, setSelectedGrants] = useState(new Set()); // set of "ClassName-SectionName"
  const [schoolClasses, setSchoolClasses] = useState([]); // Database-driven classes for this tenant
  const [changeReason, setChangeReason] = useState('');
  const [activeTab, setActiveTab] = useState('PERMISSIONS'); // PERMISSIONS, AUDIT
  const [auditLogs, setAuditLogs] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(''); // non-critical: shown with dismiss/retry
  const [errorMsg, setErrorMsg] = useState(''); // save errors: shown in red
  const [successMsg, setSuccessMsg] = useState('');

  const getAuthToken = useCallback(() => {
    if (propToken && typeof propToken === 'string' && propToken.trim()) {
      return propToken.trim();
    }
    return (
      localStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      localStorage.getItem('jwtToken') ||
      localStorage.getItem('superAdminToken') ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('token') ||
      ''
    );
  }, [propToken]);

  const getEffectiveOrgId = useCallback(() => {
    if (orgId && typeof orgId === 'string' && orgId.trim()) return orgId.trim();
    if (teacher?.orgId) return String(teacher.orgId).trim();
    try {
      const storedOrgData = localStorage.getItem('orgData');
      if (storedOrgData) {
        const parsed = JSON.parse(storedOrgData);
        if (parsed.orgId) return String(parsed.orgId).trim();
        if (parsed.collegeId) return String(parsed.collegeId).trim();
      }
    } catch (e) {}
    return (
      localStorage.getItem('orgId') ||
      localStorage.getItem('adminOrgId') ||
      localStorage.getItem('collegeId') ||
      'SCH-02'
    );
  }, [orgId, teacher]);

  const getEffectiveTeacherId = useCallback(() => {
    return teacher?.teacherId || teacher?.id || teacher?.teacherNumericId || '';
  }, [teacher]);

  const fetchTeacherAccessDetails = useCallback(async () => {
    const activeTeacherId = getEffectiveTeacherId();
    const activeOrgId = getEffectiveOrgId();
    if (!activeTeacherId || !activeOrgId) return;

    try {
      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      };

      // 1. Fetch current access summary
      const res = await fetch(
        `${API_BASE_URL}/api/teacher/${encodeURIComponent(activeOrgId)}/${encodeURIComponent(activeTeacherId)}/access?academicYear=${encodeURIComponent(academicYear)}`,
        { headers }
      );
      const data = await res.json();

      if (data && data.success) {
        if (data.accessMode) setAccessMode(data.accessMode);
        if (data.teachingWing) setTeachingWing(data.teachingWing);

        if (Array.isArray(data.allSchoolClasses) && data.allSchoolClasses.length > 0) {
          setSchoolClasses(data.allSchoolClasses);
        }

        const grants = new Set();
        if (Array.isArray(data.classes)) {
          data.classes.forEach(c => {
            if (Array.isArray(c.sections)) {
              c.sections.forEach(s => {
                grants.add(`${c.className}-${s}`);
              });
            }
          });
        }

        const effectiveMode = data.accessMode || accessMode;
        const effectiveWing = data.teachingWing || teachingWing;
        if (effectiveMode === 'WING_SCOPED' && effectiveWing) {
          const filteredGrants = new Set();
          grants.forEach(grant => {
            const dashIdx = grant.lastIndexOf('-');
            const cName = dashIdx > 0 ? grant.substring(0, dashIdx) : grant;
            if (isClassInWingCategory(cName, effectiveWing)) {
              filteredGrants.add(grant);
            }
          });
          setSelectedGrants(filteredGrants);
          setLoadError('');
          return;
        }

        setSelectedGrants(grants);
        setLoadError(''); // clear any previous load error on success
      } else if (data && !data.success) {
        setLoadError(data.error || data.message || 'Could not load current permissions');
      }

      // 2. Fetch audit logs
      try {
        const auditRes = await fetch(
          `${API_BASE_URL}/api/teacher/${encodeURIComponent(activeOrgId)}/${encodeURIComponent(activeTeacherId)}/access/audit`,
          { headers }
        );
        const auditData = await auditRes.json();
        if (auditData && auditData.audits) {
          setAuditLogs(auditData.audits);
        }
      } catch (auditErr) {
        console.warn('[TeacherAccessModal] Could not load audit logs:', auditErr);
      }
    } catch (err) {
      console.warn('[TeacherAccessModal] Could not load access details:', err);
      setLoadError('Server unreachable. Existing permissions could not be loaded. You can still configure and save.');
    }
  }, [getEffectiveTeacherId, getEffectiveOrgId, academicYear]);

  // Sync state when teacher or modal opens
  useEffect(() => {
    if (!isOpen || !teacher) return;

    setAccessMode(teacher.accessMode || 'WING_SCOPED');
    setTeachingWing(teacher.teachingWing || 'PRIMARY');
    setChangeReason('');
    setLoadError('');
    setErrorMsg('');
    setSuccessMsg('');
    setActiveTab('PERMISSIONS');

    // Fetch existing access and audit
    fetchTeacherAccessDetails();
  }, [isOpen, teacher, fetchTeacherAccessDetails]);

  // Auto-fill grants when selecting a Wing in WING_SCOPED mode
  const handleWingSelect = (wingId) => {
    setTeachingWing(wingId);
    const activeList = schoolClasses.length > 0 ? schoolClasses : STANDARD_SCHOOL_CLASSES;
    const newGrants = new Set();
    activeList.forEach(cls => {
      if (isClassInWingCategory(cls.name, wingId)) {
        cls.sections.forEach(sec => {
          newGrants.add(`${cls.name}-${sec}`);
        });
      }
    });
    setSelectedGrants(newGrants);
  };

  const toggleGrant = (className, sectionName) => {
    const key = `${className}-${sectionName}`;
    const next = new Set(selectedGrants);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    setSelectedGrants(next);
  };

  const toggleWholeClass = (className, sections) => {
    const allSelected = sections.every(sec => selectedGrants.has(`${className}-${sec}`));
    const next = new Set(selectedGrants);
    if (allSelected) {
      sections.forEach(sec => next.delete(`${className}-${sec}`));
    } else {
      sections.forEach(sec => next.add(`${className}-${sec}`));
    }
    setSelectedGrants(next);
  };

  const handleSave = async () => {
    const activeOrgId = getEffectiveOrgId();
    const activeTeacherId = getEffectiveTeacherId();

    if (!activeTeacherId) {
      setErrorMsg('Teacher ID is missing. Cannot save permissions.');
      return;
    }
    if (!activeOrgId) {
      setErrorMsg('Organization ID is missing. Please re-login.');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    setSuccessMsg('');

    try {
      const token = getAuthToken();
      if (!token) {
        setErrorMsg('Authentication session expired. Please re-login to save permissions.');
        setSaving(false);
        return;
      }
      const headers = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      };

      // Parse grants: key format is "ClassName-SectionName" — use lastIndexOf to handle class names with hyphens
      const classSections = Array.from(selectedGrants).map(item => {
        const dashIdx = item.lastIndexOf('-');
        const className = dashIdx > 0 ? item.substring(0, dashIdx) : item;
        const sectionName = dashIdx > 0 ? item.substring(dashIdx + 1) : 'A';
        return { className, sectionName };
      });

      const payload = {
        accessMode,
        teachingWing,
        academicYear: academicYear || '2026-2027',
        classSections,
        changeReason: changeReason || 'Admin updated teacher class access'
      };

      console.log('[TeacherAccessModal] Sending PUT access request:', {
        url: `${API_BASE_URL}/api/teacher/${encodeURIComponent(activeOrgId)}/${encodeURIComponent(activeTeacherId)}/access`,
        payload
      });

      const res = await fetch(
        `${API_BASE_URL}/api/teacher/${encodeURIComponent(activeOrgId)}/${encodeURIComponent(activeTeacherId)}/access`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(payload)
        }
      );

      const resData = await res.json();
      console.log('[TeacherAccessModal] PUT response:', resData);

      if (res.ok && resData && resData.success) {
        setSuccessMsg('✅ Permissions updated successfully!');
        if (onSaveSuccess) onSaveSuccess(resData);
        setTimeout(() => {
          onClose();
        }, 600);
      } else {
        setErrorMsg(resData?.error || resData?.message || 'Failed to update access permissions');
      }
    } catch (err) {
      console.error('[TeacherAccessModal] Error updating access permissions:', err);
      setErrorMsg(err.message || 'Error updating access permissions');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen || !teacher) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '780px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>👨‍🏫</span>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#ffffff' }}>
                Teacher Class & Section Access Control
              </h3>
            </div>
            <div style={{ fontSize: '12.5px', color: '#94a3b8', marginTop: '4px' }}>
              Faculty: <strong style={{ color: '#38bdf8' }}>{teacher.teacherName}</strong> ({teacher.teacherId || teacher.id}) • Academic Year: <strong style={{ color: '#fbbf24' }}>{academicYear}</strong>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '8px',
              color: '#ffffff',
              width: '32px',
              height: '32px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '18px'
            }}
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: '#f8fafc', padding: '0 24px' }}>
          <button
            type="button"
            onClick={() => setActiveTab('PERMISSIONS')}
            style={{
              padding: '12px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'PERMISSIONS' ? '3px solid #2563eb' : '3px solid transparent',
              color: activeTab === 'PERMISSIONS' ? '#2563eb' : '#64748b',
              fontWeight: activeTab === 'PERMISSIONS' ? '700' : '500',
              cursor: 'pointer',
              fontSize: '13.5px'
            }}
          >
            🛡️ Access Permissions & Scope
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('AUDIT')}
            style={{
              padding: '12px 16px',
              border: 'none',
              background: 'none',
              borderBottom: activeTab === 'AUDIT' ? '3px solid #2563eb' : '3px solid transparent',
              color: activeTab === 'AUDIT' ? '#2563eb' : '#64748b',
              fontWeight: activeTab === 'AUDIT' ? '700' : '500',
              cursor: 'pointer',
              fontSize: '13.5px'
            }}
          >
            📜 Permission Audit History ({auditLogs.length})
          </button>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '24px', overflowY: 'auto', flex: 1, backgroundColor: '#ffffff' }}>

          {/* Load error: soft amber banner with dismiss + retry */}
          {loadError && (
            <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', color: '#92400e', marginBottom: '16px', fontSize: '12.5px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
              <span>⚠️ {loadError}</span>
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button
                  type="button"
                  onClick={() => { setLoadError(''); fetchTeacherAccessDetails(); }}
                  style={{ fontSize: '11.5px', fontWeight: '700', color: '#92400e', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '5px', padding: '3px 10px', cursor: 'pointer' }}
                >
                  🔄 Retry
                </button>
                <button
                  type="button"
                  onClick={() => setLoadError('')}
                  style={{ fontSize: '13px', color: '#92400e', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1, padding: '2px 6px' }}
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            </div>
          )}

          {/* Save error: red banner */}
          {errorMsg && (
            <div style={{ padding: '12px 16px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#b91c1c', marginBottom: '16px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>⚠️ {errorMsg}</span>
              <button type="button" onClick={() => setErrorMsg('')} style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: '14px', padding: '0 4px' }}>✕</button>
            </div>
          )}

          {successMsg && (
            <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', color: '#15803d', marginBottom: '16px', fontSize: '13px' }}>
              {successMsg}
            </div>
          )}

          {activeTab === 'PERMISSIONS' ? (
            <div>
              {/* Step 1: Access Mode Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                  1. Select Access Authorization Mode
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                  {/* Option 1: ALL_CLASSES */}
                  <div
                    onClick={() => setAccessMode('ALL_CLASSES')}
                    style={{
                      border: accessMode === 'ALL_CLASSES' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      background: accessMode === 'ALL_CLASSES' ? '#eff6ff' : '#ffffff',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <input type="radio" checked={accessMode === 'ALL_CLASSES'} onChange={() => setAccessMode('ALL_CLASSES')} />
                      <strong style={{ fontSize: '13px', color: '#0f172a' }}>🌟 All Classes</strong>
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginLeft: '22px' }}>
                      Unrestricted access across the entire school
                    </div>
                  </div>

                  {/* Option 2: WING_SCOPED */}
                  <div
                    onClick={() => setAccessMode('WING_SCOPED')}
                    style={{
                      border: accessMode === 'WING_SCOPED' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      background: accessMode === 'WING_SCOPED' ? '#eff6ff' : '#ffffff',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <input type="radio" checked={accessMode === 'WING_SCOPED'} onChange={() => setAccessMode('WING_SCOPED')} />
                      <strong style={{ fontSize: '13px', color: '#0f172a' }}>🎒 Wing Based</strong>
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginLeft: '22px' }}>
                      Primary, High School, or Middle Wing
                    </div>
                  </div>

                  {/* Option 3: ALLOCATED_ONLY */}
                  <div
                    onClick={() => setAccessMode('ALLOCATED_ONLY')}
                    style={{
                      border: accessMode === 'ALLOCATED_ONLY' ? '2px solid #2563eb' : '1px solid #cbd5e1',
                      background: accessMode === 'ALLOCATED_ONLY' ? '#eff6ff' : '#ffffff',
                      borderRadius: '10px',
                      padding: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <input type="radio" checked={accessMode === 'ALLOCATED_ONLY'} onChange={() => setAccessMode('ALLOCATED_ONLY')} />
                      <strong style={{ fontSize: '13px', color: '#0f172a' }}>🎯 Specific Classes</strong>
                    </div>
                    <div style={{ fontSize: '11.5px', color: '#64748b', marginLeft: '22px' }}>
                      Strictly allocated classes & sections only
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 2: Teaching Wing Selector */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '700', color: '#1e293b', marginBottom: '8px' }}>
                  2. Teaching Level / Wing Assignment
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
                  {WING_CONFIGS.map(wing => (
                    <button
                      key={wing.id}
                      type="button"
                      onClick={() => handleWingSelect(wing.id)}
                      style={{
                        padding: '10px 8px',
                        borderRadius: '8px',
                        border: teachingWing === wing.id ? '2px solid #2563eb' : '1px solid #e2e8f0',
                        background: teachingWing === wing.id ? '#eff6ff' : '#f8fafc',
                        color: teachingWing === wing.id ? '#1d4ed8' : '#334155',
                        fontWeight: teachingWing === wing.id ? '700' : '600',
                        fontSize: '12px',
                        cursor: 'pointer',
                        textAlign: 'center',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        alignItems: 'center'
                      }}
                    >
                      <span>{wing.label}</span>
                      <span style={{ fontSize: '10.5px', color: '#64748b', fontWeight: 'normal' }}>{wing.sub}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Step 3: Interactive Class + Section Matrix */}
              {accessMode !== 'ALL_CLASSES' && (
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '700', color: '#1e293b', margin: 0 }}>
                      3. Allowed Classes & Sections Checkbox Matrix
                    </label>
                    <div style={{ fontSize: '12px', color: '#64748b' }}>
                      Click class header to select all sections
                    </div>
                  </div>

                  <div style={{ border: '1px solid #e2e8f0', borderRadius: '10px', padding: '12px', maxHeight: '220px', overflowY: 'auto', background: '#f8fafc' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '10px' }}>
                      {(() => {
                        // In WING_SCOPED mode, show only the current wing's classes from this school
                        // In ALLOCATED_ONLY mode, show all classes of this school for fine-grained picking
                        const activeList = schoolClasses.length > 0 ? schoolClasses : STANDARD_SCHOOL_CLASSES;
                        const classesToShow = accessMode === 'WING_SCOPED'
                          ? activeList.filter(cls => isClassInWingCategory(cls.name, teachingWing))
                          : activeList;
                        return classesToShow;
                      })().map(cls => {
                        const isAllSecSelected = cls.sections.every(s => selectedGrants.has(`${cls.name}-${s}`));
                        const hasSomeSecSelected = cls.sections.some(s => selectedGrants.has(`${cls.name}-${s}`));


                        return (
                          <div
                            key={cls.name}
                            style={{
                              background: hasSomeSecSelected ? '#ffffff' : '#f1f5f9',
                              border: hasSomeSecSelected ? '1.5px solid #93c5fd' : '1px solid #e2e8f0',
                              borderRadius: '8px',
                              padding: '8px 10px'
                            }}
                          >
                            <div
                              onClick={() => toggleWholeClass(cls.name, cls.sections)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                cursor: 'pointer',
                                fontWeight: '700',
                                fontSize: '13px',
                                color: hasSomeSecSelected ? '#1e40af' : '#475569',
                                borderBottom: '1px dashed #e2e8f0',
                                paddingBottom: '4px',
                                marginBottom: '6px'
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={isAllSecSelected}
                                onChange={() => {}}
                              />
                              <span>Class {cls.name}</span>
                            </div>

                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              {cls.sections.map(sec => {
                                const isChecked = selectedGrants.has(`${cls.name}-${sec}`);
                                return (
                                  <label
                                    key={sec}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      fontSize: '12px',
                                      fontWeight: isChecked ? '700' : '500',
                                      color: isChecked ? '#2563eb' : '#64748b',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => toggleGrant(cls.name, sec)}
                                    />
                                    Sec {sec}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {/* Live Permission Preview Summary Banner */}
              <div
                style={{
                  background: accessMode === 'ALL_CLASSES' ? '#fef3c7' : '#eff6ff',
                  border: accessMode === 'ALL_CLASSES' ? '1px solid #fcd34d' : '1px solid #bfdbfe',
                  borderRadius: '10px',
                  padding: '12px 16px',
                  marginBottom: '16px'
                }}
              >
                <div style={{ fontWeight: '700', fontSize: '13.5px', color: accessMode === 'ALL_CLASSES' ? '#92400e' : '#1e40af' }}>
                  {accessMode === 'ALL_CLASSES' ? (
                    '⚠️ Unrestricted Institution-Wide Access'
                  ) : (
                    `🟢 Teacher ${teacher.teacherName} can access ${selectedGrants.size} classes/sections`
                  )}
                </div>
                <div style={{ fontSize: '12px', color: accessMode === 'ALL_CLASSES' ? '#b45309' : '#3b82f6', marginTop: '4px' }}>
                  {accessMode === 'ALL_CLASSES' ? (
                    'This teacher can view and manage students from all classes across the school.'
                  ) : selectedGrants.size > 0 ? (
                    `Permitted: ${Array.from(selectedGrants).join(', ')}`
                  ) : (
                    '⚠️ No classes selected. Teacher will not be able to access any student records.'
                  )}
                </div>
              </div>

              {/* Change Reason Note */}
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#475569', marginBottom: '4px' }}>
                  Admin Reason / Audit Note (Optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. Assigned as primary mathematics and science educator for 2026-2027"
                  value={changeReason}
                  onChange={(e) => setChangeReason(e.target.value)}
                  style={{ width: '100%', fontSize: '12.5px', height: '36px' }}
                />
              </div>
            </div>
          ) : (
            /* Audit Log Tab */
            <div>
              {auditLogs.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '30px', color: '#64748b' }}>
                  No previous permission changes recorded for this teacher.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {auditLogs.map((log, idx) => (
                    <div
                      key={log.id || idx}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        padding: '12px',
                        background: '#f8fafc'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <strong style={{ fontSize: '12.5px', color: '#0f172a' }}>
                          Changed By: {log.changedBy || 'Admin'}
                        </strong>
                        <span style={{ fontSize: '11.5px', color: '#64748b' }}>
                          {log.changedAt ? new Date(log.changedAt).toLocaleString() : 'Recent'}
                        </span>
                      </div>
                      <div style={{ fontSize: '12px', color: '#334155' }}>
                        Mode: <span style={{ color: '#2563eb', fontWeight: '600' }}>{log.previousAccessMode || '—'}</span> ➔ <span style={{ color: '#16a34a', fontWeight: '600' }}>{log.newAccessMode || '—'}</span> | Wing: <span style={{ fontWeight: '600' }}>{log.newWing || '—'}</span>
                      </div>
                      {log.newAllocations && (
                        <div style={{ fontSize: '11.5px', color: '#64748b', marginTop: '4px' }}>
                          Allocations: {log.newAllocations}
                        </div>
                      )}
                      {log.changeReason && (
                        <div style={{ fontSize: '11.5px', fontStyle: 'italic', color: '#475569', marginTop: '2px' }}>
                          Note: "{log.changeReason}"
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div
          style={{
            padding: '14px 24px',
            background: '#f8fafc',
            borderTop: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px'
          }}
        >
          <button
            type="button"
            className="fs-btn-secondary"
            onClick={onClose}
            disabled={saving}
            style={{ padding: '8px 16px', fontSize: '13px' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="fs-btn-primary"
            onClick={handleSave}
            disabled={saving}
            style={{
              padding: '8px 20px',
              fontSize: '13px',
              fontWeight: '700',
              background: '#2563eb',
              color: '#ffffff',
              border: 'none',
              borderRadius: '6px',
              cursor: saving ? 'not-allowed' : 'pointer'
            }}
          >
            {saving ? 'Saving Permissions...' : '💾 Save Class Permissions'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherClassAccessModal;
